import React, { FC } from 'react'
import { withRouter } from 'react-router-dom'
import { Menu, Segment, Grid } from 'semantic-ui-react'
import isEqual from 'lodash/isEqual'
import { number as numberFormatter, date as dateFormatter } from 'loankeeper-common/helpers/formatters'
import dateComparer from 'loankeeper-common/helpers/dateComparer'
import { translate } from 'loankeeper-common/helpers'
import { colors } from 'shared'
//@ts-ignore
import LKButton from 'shared/LKButton'
import { LKIcon } from 'loankeeper-common/components'
//@ts-ignore
import LKGrid from 'shared/LKGrid'
//@ts-ignore
import withConstantPermissions from 'shared/containers/withConstantPermissions'
//@ts-ignore
import { GridContainer } from 'shared/StyledComponents'
import { translateCmd, translateBase, icon } from '../helpers'
import { formName } from '../constants'
import { constants as DCConstants } from '../../DetailsContainer'
import { sortedLoansSelector } from '../selectors'
import { MemoizedGridProps, LoanProps } from '../types'
/* eslint-disable no-param-reassign */

const numberValueColumnOptions = [
  'Amount',
  'Interest',
  'TotalDue',
  'OSBalance',
  'PenaltyInt',
  'PenaltyPri',
  'Principal',
  'ProcFee',
].reduce((config, key) => {
  config[key] = {
    valueFormatter: ({ value }) => numberFormatter(value),
    cellStyle: { textAlign: 'right' },
  }

  return config
}, {})

const CurrentLoansConfig = {
  gridIdentity: { formName: 'frm_client_briefing' }, // , objectName: 'dbgrd_brief'
  columnConfigs: {
    ...numberValueColumnOptions,
    OVDDays: { cellStyle: { textAlign: 'right' } },
    DisburseDate: { comparator: dateComparer, valueFormatter: ({ value }) => dateFormatter(value) },
    Expired: { iconComponent: (value: number) => (value !== 0 ? 'clock' : null) },
    Stopped: { iconComponent: (value: number) => (value !== 0 ? 'stop' : null) },
    Secured: { iconComponent: (value: number) => (value !== 0 ? 'life_ring' : null) },
  },
  enableColumnsResize: true,
  enableColumnsOrder: true,
  enableColumnsHide: false,
  enableSorting: true,
  enableTranslate: true,
  enableRowSelection: true,
  syncGridState: false, // true,

  getRowStyle: ({ node: { data } }) => {
    if (data.ClientID === 0 && data.LoanID === 0) {
      return { backgroundColor: colors.grey }
    }

    switch (data.StateID) {
      case 2:
        return { backgroundColor: colors.grey }
      case 4:
        return { backgroundColor: colors.black, color: '#fff' }
      case 6:
        return { backgroundColor: colors.yellow }
      default:
        return { backgroundColor: 'white' }
    }
  },
}

const LoansGridConfig = {
  gridIdentity: { formName, objectName: 'dbgrd_loans' },
  columnConfigs: {
    Amount: { valueFormatter: ({ value }) => numberFormatter(value), cellStyle: { textAlign: 'right' } },
    DisburseDate: { comparator: dateComparer, valueFormatter: ({ value }) => dateFormatter(value) },
    Expired: { iconComponent: (value) => (value !== 0 ? 'clock' : null) },
    Stopped: { iconComponent: (value) => (value !== 0 ? 'stop' : null) },
    Secured: { iconComponent: (value) => (value !== 0 ? 'life_ring' : null) },
    CoverDate: { valueFormatter: ({ value }) => dateFormatter(value) },
    SortID: { sort: 'asc' },
  },
  enableColumnsResize: true,
  enableColumnsOrder: true,
  enableColumnsHide: false,
  enableRowSelection: true,
  enableSorting: true,
  getRowStyle: ({
    node: {
      data: { StateID },
    },
  }) => {
    switch (StateID) {
      case 2:
        return { backgroundColor: colors.grey }
      case 4:
        return { backgroundColor: colors.black, color: '#fff' }
      case 6:
        return { backgroundColor: colors.yellow }
      default:
        return { backgroundColor: 'white' }
    }
  },
}

const MemoizedLoanGrid: FC<MemoizedGridProps> = React.memo(({ ID, rowData, onRowDoubleClicked, pushDetailsRoute }) => {
  return (
    <LKGrid
      rowData={rowData}
      onRowDoubleClicked={onRowDoubleClicked}
      {...LoansGridConfig}
      getContextMenuItems={({ node: { data } }) => [
        {
          name: translateCmd('view_loan'),
          action: () => pushDetailsRoute(data),
          icon: 'info',
          disabled: !data.ID,
          permissionKey: 'VIEW_LOAN',
        },
        'excelExport',
      ]}
      instanceName={`${DCConstants.DetailsGridPrefixes.client}-${ID}-Loans`}
    />
  )
})

const MemoizedBriefingGrid: FC<MemoizedGridProps> = React.memo(
  ({ ID, rowData, onRowDoubleClicked, pushDetailsRoute }) => (
    <LKGrid
      rowData={rowData}
      onRowDoubleClicked={onRowDoubleClicked}
      {...CurrentLoansConfig}
      getContextMenuItems={({ node: { data } }) => [
        {
          name: translateCmd('view_loan'),
          action: () => pushDetailsRoute({ ID: data.LoanID, ...data }),
          icon: 'info',
          disabled: !data.LoanID,
          permissionKey: 'VIEW_LOAN',
        },

        'excelExport',
      ]}
      instanceName={`${DCConstants.DetailsGridPrefixes.client}-${ID}-CurrentLoans`}
    />
  ),
)

@withRouter
@withConstantPermissions('VIEW_LOAN as CanView')
class Loans extends React.Component<LoanProps> {
  shouldComponentUpdate(prevProps: LoanProps) {
    return !isEqual(this.props, prevProps)
  }

  onRowDoubleClickedCurrentLoans = ({ node: { data } }) => {
    if (this.props.CanView) {
      this.pushDetailsRoute({ ID: data.LoanID, ...data })
    }
  }

  onRowDoubleClickedLoans = ({ node: { data } }) => {
    if (this.props.CanView) {
      this.pushDetailsRoute(data)
    }
  }

  pushDetailsRoute = ({ ID, HashSum_LoanID_ }) => {
    if (ID) {
      this.props.history.push(`/secure/loan/${ID}/details/${HashSum_LoanID_}`)
    }
  }

  render() {
    const {
      ID,
      data: { loans, briefing },
    } = this.props
    const sortedLoans = sortedLoansSelector(loans)

    return (
      <div className="u-fluid u-flex u-directionColumn" style={{ padding: '10px' }}>
        <div className="u-marginBottom-10 u-fluid u-relative">
          <MemoizedLoanGrid
            ID={ID}
            rowData={sortedLoans}
            onRowDoubleClicked={this.onRowDoubleClickedLoans}
            pushDetailsRoute={this.pushDetailsRoute}
          />
        </div>

        <div className="u-marginBottom-10 u-flex u-directionColumn u-fluid">
          <Menu attached="top">
            <Menu.Item className="u-noPadding u-paddingRight-15 u-paddingLeft-5">
              <LKIcon alias="loan_double" size={30} style={{ marginRight: 5 }} />

              {translate('frm_client_briefing-frm_client_briefing')}
            </Menu.Item>
          </Menu>

          <Segment className="u-noPadding u-fluid u-overflowAuto" attached="bottom">
            <MemoizedBriefingGrid
              ID={ID}
              rowData={briefing}
              onRowDoubleClicked={this.onRowDoubleClickedCurrentLoans}
              pushDetailsRoute={this.pushDetailsRoute}
            />
          </Segment>
        </div>

        <GridContainer>
          <Grid>
            <Grid.Row>
              <Grid.Column textAlign="left" width={2} />

              <Grid.Column textAlign="right" width={14}>
                <LKButton alias="print" size="tiny">
                  {translateCmd('print_credit_history')}
                </LKButton>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </GridContainer>
      </div>
    )
  }
}

//@ts-ignore
Loans.config = {
  title: () => translateBase('tb_loans'),
  name: 'loans',
  component: Loans,
  api: ['loans', 'briefing'],
  icon: icon('tb_loans'),
}
//@ts-ignore
Loans.defaultProps = {
  data: [],
}

export default Loans
